/**
 * BalanceHistoryModal Component
 * Modal for displaying account balance history
 * WP-FLOW-10: Balance history modal feature
 * WP-FLOW-11: Added chart visualization with tab interface
 * WP-FLOW-12: Added Add Entry functionality
 */
import { useState } from 'react';
import Button from '@/components/ui/Button';
import BalanceHistoryTable from './BalanceHistoryTable';
import BalanceHistoryChart from './BalanceHistoryChart';
import BalanceEntryForm from './BalanceEntryForm';
import type { AccountWithConnection } from '@/services/accounts';
import { cn } from '@/utils/cn';

export interface BalanceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountWithConnection | null;
}

type TabType = 'chart' | 'table';

export default function BalanceHistoryModal({
  isOpen,
  onClose,
  account,
}: BalanceHistoryModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('chart');
  const [showAddForm, setShowAddForm] = useState(false);

  if (!isOpen || !account) return null;

  const handleAddSuccess = () => {
    setShowAddForm(false);
    // Switch to table view to see the new entry
    setActiveTab('table');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="balance-history-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <ClockIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="balance-history-modal-title"
                className="text-lg font-semibold text-gray-900 truncate"
              >
                {account.name} - Balance History
              </h2>
              <p className="text-sm text-gray-500">
                {account.currency}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              aria-label="Close modal"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {showAddForm ? (
            /* Add Entry Form */
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-4">
                Add Balance Entry
              </h3>
              <BalanceEntryForm
                accountId={account.id}
                onSuccess={handleAddSuccess}
                onCancel={() => setShowAddForm(false)}
              />
            </div>
          ) : (
            <>
              {/* Tab Buttons */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('chart')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                    activeTab === 'chart'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  )}
                >
                  Chart
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('table')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                    activeTab === 'table'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  )}
                >
                  Table
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'chart' ? (
                <BalanceHistoryChart
                  accountId={account.id}
                  currency={account.currency}
                />
              ) : (
                <BalanceHistoryTable
                  accountId={account.id}
                  currency={account.currency}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-between">
            {!showAddForm && (
              <Button
                type="button"
                variant="primary"
                onClick={() => setShowAddForm(true)}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Entry
              </Button>
            )}
            <div className={showAddForm ? 'ml-auto' : ''}>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  );
}
